import React, { ComponentProps, useCallback, useEffect, useRef } from 'react';

import { useSnackbar } from 'notistack';
import { TourDetailsFormResources, useTourDetailsFormQuery } from '~/queries/tours/useTourDetailsFormQueries';

import { Alert, AlertTitle, Button, CircularProgress } from '@mui/material';

import ExtendedRJSForm from '../RJSF/ExtendedRJSForm';

interface Props {
  tourId: string;
}

const IMMEDIATE_PERSIST_SOURCES = new Set([
  'handle-array-item-copy',
  'handle-parent-sibling-array-copy',
  'shared-image:delete',
  'shared-image:delete-undo',
  'shared-image:hidden-toggle',
  'shared-image:hero-toggle',
  'shared-image:reorder',
  'shared-image:upload',
]);

export default function TourImagesDetailsForm(props: Props) {
  const { tourId } = props;
  const { enqueueSnackbar } = useSnackbar();
  const persistTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const latestFormDataRef = useRef<unknown>(null);
  const lastPatchModeRef = useRef<'autosave' | 'submit'>('submit');

  const { fetchRequestInstance, fetch, patchRequestInstance, patch } = useTourDetailsFormQuery(
    TourDetailsFormResources.TOUR_IMAGES,
  );

  useEffect(() => {
    fetch(tourId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tourId]);

  const handleSubmission = useCallback<ComponentProps<typeof ExtendedRJSForm>['onSubmit']>(
    ({ formData }) => {
      if (persistTimerRef.current) {
        clearTimeout(persistTimerRef.current);
        persistTimerRef.current = null;
      }
      lastPatchModeRef.current = 'submit';
      patch(tourId, formData);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [tourId],
  );

  const handleFormDataChange = useCallback<ComponentProps<typeof ExtendedRJSForm>['onFormDataChange']>(
    ({ formData, meta }) => {
      latestFormDataRef.current = formData;

      if (!IMMEDIATE_PERSIST_SOURCES.has(meta.source)) {
        return;
      }

      if (persistTimerRef.current) {
        clearTimeout(persistTimerRef.current);
      }

      persistTimerRef.current = setTimeout(() => {
        lastPatchModeRef.current = 'autosave';

        persistTimerRef.current = null;
        patch(tourId, latestFormDataRef.current);
      }, 400);
    },
    [patch, tourId],
  );

  useEffect(
    () => () => {
      if (persistTimerRef.current) {
        clearTimeout(persistTimerRef.current);
        persistTimerRef.current = null;
        patch(tourId, latestFormDataRef.current);
      }
    },
    [patch, tourId],
  );

  useEffect(() => {
    switch (patchRequestInstance.status) {
      case 'succeeded':
        if (lastPatchModeRef.current === 'submit') {
          enqueueSnackbar('Tour images updated successfully.', {
            variant: 'success',
          });
        }
        break;
      case 'failed':
        enqueueSnackbar(`Submission failed! ${patchRequestInstance.error}`, {
          variant: 'error',
        });
        break;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patchRequestInstance.status, enqueueSnackbar]);

  if (fetchRequestInstance.status === 'pending' || fetchRequestInstance.status === 'uninitiated') {
    return <CircularProgress />;
  }

  if (fetchRequestInstance.status === 'failed') {
    return (
      <Alert
        severity="error"
        action={
          <Button
            color="inherit"
            variant="outlined"
            onClick={() => {
              fetch(tourId);
            }}
          >
            Retry
          </Button>
        }
      >
        <AlertTitle>Failed to fetch the form.</AlertTitle>
        {fetchRequestInstance.error}
      </Alert>
    );
  }

  const { result } = fetchRequestInstance;
  return (
    <>
      <ExtendedRJSForm
        id="tour-images-details-form"
        schema={result.schema}
        uiSchema={result.uiSchema}
        formData={result.formData}
        onSubmit={handleSubmission}
        onFormDataChange={handleFormDataChange}
        busy={patchRequestInstance.status === 'pending' && lastPatchModeRef.current === 'submit'}
        useSharedImageArrayEditor
      />
      {patchRequestInstance.status === 'failed' && <Alert severity="error">{patchRequestInstance.error}</Alert>}
    </>
  );
}
